import { clone, compare } from '../../../webmodule-common/other/clone';
import { customElement, property, query, state } from 'lit/decorators.js';
import { dealerTokenProvider } from '../../../webmodule-common/other/api/dealer-api-communications';
import { getApiFactory } from '../../api/api-injector';
import { getApiToken } from '../../../webmodule-common/other/api/current-user';
import { getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import { html, nothing, PropertyValueMap } from 'lit';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import { LangDealerFranchiseeConversationWmConversationEntryDropzone } from '../../../localization/scripts/dealer-franchisee/conversation/wm-conversation-entry-dropzone-lang';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import {
  fileFromClipboardData,
  PASTE_TARGET_SELECTOR,
  readImageFromSystemClipboard,
  shouldClaimPaste
} from '../../../webmodule-common/other/ui/templateresult/form-input-image-clipboard';
import type { ContextMenuAction } from '../../../webmodule-common/other/ui/wm-context-menu';
import '../../../webmodule-common/other/ui/wm-context-menu';
import {
  ResultConversationUploadAttachment,
  ViewConversationAttachment
} from '../../api/dealer-api-interface-franchisee';

// Legacy guard removed.

interface UploadInfo {
  uuid: string;
  filename: string;
  chunked?: boolean;
}

interface DropZoneFileObject {
  publicUrl?: string;
  size?: number;
  accepted?: boolean;
  uuid?: string;
  name: string;
  xhr?: XMLHttpRequest;
  previewElement: HTMLElement;
  previewTemplate: HTMLElement;
  status?: string;
  upload?: UploadInfo;
}

@customElement('wmview-conversation-attachments')
export class WebModuleConversationEntryDropZone extends LitElementBase {
  removeDropZoneFile(name: string) {
    const file = this.dropzoneFileObjects.find(x => x.name === name);
    if (file) {
      this.dropzone?.removeFile(file);
    }
  }

  private cleanUpProcessActivated = false;
  dropzone: any;
  @property({ type: String }) caption = LangDealerFranchiseeConversationWmConversationEntryDropzone.attachments();
  @property({ type: String }) conversationId = '';
  @property({ attribute: false })
  private _conversationEntryId = '';
  private _existingFileProcess = 0;

  private get isInternalUpdate() {
    return this._existingFileProcess > 0;
  }

  public get conversationEntryId() {
    return this._conversationEntryId;
  }

  public set conversationEntryId(value) {
    if (value !== this._conversationEntryId) {
      this._conversationEntryId = value;
      this.resetAttachments([]);
    }
  }

  private log(s: any) {
    console.log(`ATTACHMENT_DROP_${this._internalId}:${s}`);
  }

  private _internalId = getInternalId();

  @property({ attribute: false })
  private _existingFiles: ViewConversationAttachment[] = [];
  public get existingFiles(): ViewConversationAttachment[] {
    return this._existingFiles;
  }

  public set existingFiles(value: ViewConversationAttachment[]) {
    this.log('existingFiles called');
    if (!compare(value, this.existingFiles)) {
      this.log('existingFiles modified');
      this.resetAttachments(value);
    }
  }

  @property({ type: Number }) maxFiles = 20;
  /**
   * Shown in place of the upload button while readonly, to say WHY there is nothing to press (the
   * ETO editor sets "Supplier to upload" for a dealer). Empty leaves the bar there but blank, which
   * is what a zone that already holds its file wants.
   */
  @property({ type: String }) readonlyCaption = '';
  @property({ type: Boolean }) trimTopWhenFileExceeded = true;
  @property({ type: Boolean }) allowDownloadLinks = false;
  @query('#form-upload')
  form?: HTMLFormElement;
  @query('#clickable')
  clickable?: HTMLDivElement;
  // Only this button opens the file dialog; the zone itself stays droppable but not clickable.
  @query('#upload-action')
  uploadAction?: HTMLElement;
  @query('#previews')
  previews?: HTMLDivElement;
  chunkRetryCount: number;
  chunkSizeInBytes: number;
  uploadUrl: string;
  uploadLimitInMB: any;
  parallelUploadCount: any;

  private dropzoneFileObjects: DropZoneFileObject[] = [];

  async activateCleanupProcess() {
    try {
      if (!this.cleanUpProcessActivated) {
        this.cleanUpProcessActivated = true;
        await getApiFactory().franchisee().queueCleanupConversationAttachments({
          conversationEntryId: this.conversationEntryId,
          conversationId: this.conversationId
        });
      }
    } catch (e) {
      this.cleanUpProcessActivated = false;
      console.log(e);
    }
  }

  public get allDropZoneFiles() {
    return this.dropzoneFileObjects;
  }

  public get allFileNames() {
    return this.existingFiles.map(x => x.filename);
  }

  public get allFilesAsInputs() {
    return this.existingFiles.map(x => {
      return { filename: x.filename };
    });
  }

  constructor() {
    super();

    this.log('created');

    this.chunkRetryCount = 3;
    this.chunkSizeInBytes = 4 * 1024 * 1024;
    this.uploadUrl = getApiFactory().franchisee().getConversationAttachmentUploadURL;
    this.uploadLimitInMB = 200;
    this.parallelUploadCount = 3;
  }

  protected eventClick = (e: Event) => {
    if (!this.allowDownloadLinks) {
      //e.preventDefault();
      if ((e.target as HTMLElement).hasAttribute('data-dz-name')) {
        const filename = (e.target as HTMLElement).innerText;
        this.dispatchCustom('attachmentclicked', {
          filename: filename
        });
      }
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('click', this.eventClick);
    // Paste is caught on the document because a drop zone is not a text control and so never
    // receives paste itself; eventPaste decides whether this instance owns the event.
    this.addEventListener('mouseenter', this.eventPointerEnter);
    this.addEventListener('mouseleave', this.eventPointerLeave);
    document.addEventListener('paste', this.eventPaste);
    if (this.dropzone) {
      this.log('enable dropzone');
      this.setDropZoneState();
    }
  }

  private setDropZoneState() {
    if (!this.isReadonly) this.dropzone?.enable();
    else this.dropzone?.disable();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    this.removeEventListener('click', this.eventClick);
    this.removeEventListener('mouseenter', this.eventPointerEnter);
    this.removeEventListener('mouseleave', this.eventPointerLeave);
    document.removeEventListener('paste', this.eventPaste);
    // mouseleave only fires on pointer movement, so a detach while hovered would otherwise leave
    // this true and re-arm the document listener with a stale flag on the next connect.
    this.pointerInside = false;
    if (this.dropzone) {
      this.dropzone.disable();
      this.log('disable dropzone');
    }
  }

  // Whether the pointer is over this zone; used only to decide which control owns a paste.
  private pointerInside = false;

  protected eventPointerEnter = () => {
    this.pointerInside = true;
  };

  protected eventPointerLeave = () => {
    this.pointerInside = false;
  };

  /**
   * Ctrl+V over (or into) the zone, which is the only way a copied FILE can reach a web page at
   * all - the async clipboard API the Paste button uses is not allowed to see one. Unlike the image
   * uploader this takes a file of any type, because a shop drawing is a document.
   */
  protected eventPaste = async (e: ClipboardEvent) => {
    // defaultPrevented means another control already claimed this paste; only one may act.
    if (this.isReadonly || e.defaultPrevented) return;

    const active = document.activeElement;
    const claim = shouldClaimPaste({
      activeElement: active,
      focusInside: active === this || this.contains(active),
      pointerInside: this.pointerInside,
      focusedUploader: active?.closest?.(PASTE_TARGET_SELECTOR) ?? null,
      self: this
    });
    if (!claim) return;

    // Claimed, so this paste is ours whatever it turned out to hold. Doing nothing would read as a
    // broken zone rather than as the wrong thing being on the clipboard, so say which it was.
    e.preventDefault();

    const file = fileFromClipboardData(e.clipboardData);
    if (!file) {
      await information(LangDealerFranchiseeConversationWmConversationEntryDropzone.pasteNotAFile());
      return;
    }

    this.dropzone?.addFile(file);
  };

  /**
   * The "Paste" menu item. It can only ever reach image content: a click cannot produce a paste
   * event, so this has to go through the async clipboard API, which deliberately does not expose a
   * copied file. Every other outcome therefore points at Ctrl+V rather than failing silently.
   */
  protected eventPasteFromMenu = async () => {
    if (this.isReadonly) return;

    const outcome = await readImageFromSystemClipboard();
    if (outcome.status !== 'image') {
      await information(LangDealerFranchiseeConversationWmConversationEntryDropzone.clipboardHasNoFile());
      this.focusDropZone();
      return;
    }

    this.dropzone?.addFile(outcome.file);
  };

  /** The focusable drop area, so a following Ctrl+V is claimed by this zone. */
  private focusDropZone() {
    this.querySelector<HTMLElement>('.conversation-attachmments')?.focus();
  }

  /** Nothing to offer while readonly, so the browser's own menu is left alone (see wm-context-menu). */
  private contextMenuActions(): ContextMenuAction[] {
    if (this.isReadonly) return [];

    return [
      {
        id: 'choose',
        label:
          this.maxFiles === 1
            ? LangDealerFranchiseeConversationWmConversationEntryDropzone.chooseFile()
            : LangDealerFranchiseeConversationWmConversationEntryDropzone.chooseFiles(),
        icon: 'folder-open',
        // Through the bar's own control, which is the element Dropzone bound its picker to.
        run: () => this.uploadAction?.click()
      },
      {
        id: 'paste',
        label: LangDealerFranchiseeConversationWmConversationEntryDropzone.pasteFile(),
        icon: 'paste',
        run: this.eventPasteFromMenu
      }
    ];
  }

  protected previewTemplate() {
    const removeLink = this.isReadonly
      ? `<span class="d-none" data-dz-remove>&times;</span>`
      : `<span data-dz-remove>&times;</span>`;
    if (this.allowDownloadLinks)
      return `
                <div class="dz-preview dz-file-preview">
                    <div class="dz-details">
                        <div class="dz-filename">
                            ${removeLink}
                            <a href="#" target="_blank" class="file-link">
                                <i class="fa-regular fa-file-lines bi"></i>
                                <span data-dz-name class="dz-filename-text"></span
                            ></a>
                        </div>
                    </div>
                    <div
                        class="dz-progress progress progress-striped active d-none"
                        role="progressbar"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow="0"
                        style="width:100%;"
                    >
                        <span
                            class="dz-upload progress-bar progress-bar-success"
                            style="width:0%;"
                            data-dz-uploadprogress
                        ></span>
                    </div>
                    <div class="dz-error-message">
                        <span data-dz-errormessage></span>
                    </div>
                </div>
            `;
    //add html to read and edit.. ie html`
    return `
            <div class="dz-preview dz-file-preview">
                <div class="dz-details">
                    <div class="dz-filename">
                        ${removeLink}
                        <i class="fa-regular fa-file-lines bi"></i>
                        <span data-dz-name class="dz-filename-text"></span>
                    </div>
                </div>
                <div
                    class="dz-progress progress progress-striped active d-none"
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow="0"
                    style="width:100%;"
                >
                    <span
                        class="dz-upload progress-bar progress-bar-success"
                        style="width:0%;"
                        data-dz-uploadprogress
                    ></span>
                </div>
                <div class="dz-error-message">
                    <span data-dz-errormessage></span>
                </div>
            </div>
        `;
  }

  protected eventChunksUploadComplete = (file: DropZoneFileObject, done) => {
    if (file?.xhr?.response) {
      const response = JSON.parse(file.xhr.response) as ResultConversationUploadAttachment;
      if (response && response.success) {
        file.publicUrl = response.files[0].publicUrl;
        this.addDropZoneFileObject(file, response.files[0]);
      }
      this.log('chunks completed ' + file.name);
    }
    done(); // calls uploadSuccess, but response = null
  };
  protected eventUploadError = (file, response, xhr) => {
    this.log('uploaderror');
    this.dropzone.removeFile(file);
    this.internalUploadError(file, response, xhr);
  };

  protected async handleUploadCancelled(message) {
    if (message !== 'Upload cancelled.') await information(message);
    // eslint-disable-next-line no-useless-return
    else return;
  }

  protected async internalUploadError(file, response, _xhr) {
    file.previewElement?.remove();
    if (response?.message) {
      await information(response.message);
    } else if (response) {
      await this.handleUploadCancelled(response);
    } else {
      await information(LangDealerFranchiseeConversationWmConversationEntryDropzone.couldNotUploadFile());
    }
  }

  protected eventUploadSuccess = (file: DropZoneFileObject, response: ResultConversationUploadAttachment) => {
    this.eventUploadFinished(file);
    if (response?.files && response.files.length > 0) {
      // non-chunked uploads have a response
      this.bindHref(file, response.files[0]);
      this.log('fileupload success' + file.name);
    }
  };

  protected eventFileRemoved = (file: DropZoneFileObject, _response) => {
    this._existingFiles = this._existingFiles.filter(x => x.filename !== file.name);
    this.dropzoneFileObjects = this.dropzoneFileObjects.filter(x => x.name !== file.name);
    file.previewElement?.remove();
    this.dispatchUpdateEvent(); //
  };

  dispatchUpdateEvent() {
    if (this.isInternalUpdate) return;
    this.log('dispatch-changed');
    this.dispatchCustom('changed', {
      isUploading: this.isUploading,
      files: this.existingFiles
    });
  }

  protected eventUploadFinished = file => {
    this.log(`File Upload Complete`);

    if (file?.previewElement) {
      (file.previewElement.querySelector('.dz-progress') as HTMLDivElement)?.classList.add('d-none');

      (file.previewElement as HTMLDivElement)
        .querySelectorAll('[data-dz-remove]')
        .forEach(x => x.classList.remove('d-none'));
    }
  };

  protected eventAcceptFile = (file, done) => {
    if (!this.trimTopWhenFileExceeded)
      if (this.allFileNames.length >= this.maxFiles)
        return done(
          LangDealerFranchiseeConversationWmConversationEntryDropzone.maxUploadCount({ maxFiles: this.maxFiles })
        );

    const filelist = this.allFileNames;
    for (const filename of filelist) {
      if (filename === file.name) {
        this.log(`duplicate upload ${file.name}`);
        this.removeDropZoneFile(file.name);
        return done(LangDealerFranchiseeConversationWmConversationEntryDropzone.duplicateFile()); // Localized duplicate file error
      }
    }
    (file.previewElement.querySelector('.dz-progress') as HTMLDivElement).classList.remove('d-none');
    return done(); // accept
  };
  @state()
  private _isUploading = false;
  public get isUploading() {
    return this._isUploading;
  }

  public set isUploading(value) {
    if (value !== this._isUploading) {
      this._isUploading = value;
      if (!this.isInternalUpdate) {
        if (value) this.activateCleanupProcess();
      }
    }
  }

  protected onReadonlyChanged() {
    this.querySelectorAll('[data-dz-remove]').forEach((x: Element) => {
      if (!this.isReadonly) (x as HTMLElement).classList.remove('d-none');
      else (x as HTMLElement).classList.add('d-none');
    });
    this.setDropZoneState();
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    try {
      this.log('first updating');
      const urlCreator = () =>
        this.uploadUrl + `?conversationId=${this.conversationId}&conversationEntryId=${this.conversationEntryId}`;
      this.log('created dropzone');
      this.dropzone = new globalThis.Dropzone(this, {
        url: urlCreator(),
        maxFilesize: this.uploadLimitInMB,
        previewTemplate: this.previewTemplate(),
        clickable: this.uploadAction ?? this.clickable,
        previewsContainer: this.previews,
        // Was a hardcoded 30, which never took effect - eventAcceptFile already enforces this.maxFiles
        // - but it did make the file dialog multi-select even where only one file is allowed, so you
        // could pick several and only then be told off. Dropzone drops `multiple` when this is 1.
        maxFiles: this.maxFiles,
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          Authorization: `bearer ${getApiToken()}`,
          'Authorization-Client': dealerTokenProvider()
        },
        //chunked uploads disabled
        chunking: true,
        chunkSize: this.chunkSizeInBytes,
        chunksUploaded: this.eventChunksUploadComplete,
        //events
        error: this.eventUploadError,

        processing: function () {
          this.options.url = urlCreator();
        },
        accept: this.eventAcceptFile,
        success: this.eventUploadSuccess,
        removedfile: this.eventFileRemoved
      });

      this.dropzone.on('addedfile', (file: DropZoneFileObject) => {
        if (file.upload) return;
        const filename = file.name;
        const attachment = this.existingFiles?.find(x => x.filename === filename);
        if (attachment) {
          this.bindHref(file, attachment);
          this.addDropZoneFileObject(file, attachment);
        } else this.addDropZoneFileObject(file);
      });

      this.dropzone.on('queuecomplete', () => {
        this.log('queue completed');
        const accepted = this.allFileNames;
        const count = accepted.length - this.maxFiles;
        if (count > 0 && this.trimTopWhenFileExceeded) {
          for (let i = 0; i < count; i++) {
            this.removeDropZoneFile(accepted[i]);
          }
        }

        this.isUploading = false;
        this.dispatchUpdateEvent();
        this.log('dropzone all complete');
      });
      this.dropzone.on('sending', () => {
        this.isUploading = true;

        this.log('dropzone sending');
      });
      this.dropzone.on('sendingmultiple', () => {
        this.isUploading = true;
        this.log('dropzone sendingmultiple');
      });
      this.dropzone.on('processing', () => {
        this.isUploading = true;
        this.log('dropzone processing');
      });
      this.dropzone.on('success', (file: DropZoneFileObject) => {
        if (file && file.xhr && file.xhr.response) {
          const response = JSON.parse(file.xhr.response) as ResultConversationUploadAttachment;
          if (response && response.success) {
            file.publicUrl = response.files[0].publicUrl;
            this.addDropZoneFileObject(file, response.files[0]);
          }
          this.log('chunks completed ' + file.name);
        }
        //this.addDropZoneFileObject(file);

        this.log('dropzone uploaded ' + file.name);
      });

      this.log('rebuilding existing file list');
      this.rebuildExistingFilesList();
      this.setDropZoneState();
    } catch (e) {
      console.log(e);
    }
  }

  bindHref(file: DropZoneFileObject, attachment?: ViewConversationAttachment) {
    const elem = file.previewElement.querySelector('.file-link');
    const href = elem as HTMLAnchorElement;

    if (attachment) {
      file.publicUrl = attachment.publicUrl;
      if (href) href.href = attachment.publicUrl;
    }
  }

  private addDropZoneFileObject(file: DropZoneFileObject, attachment?: ViewConversationAttachment) {
    this.dropzoneFileObjects = [
      ...this.dropzoneFileObjects.filter(x => x.name !== file.name),
      file as DropZoneFileObject
    ];
    if (!this.existingFiles.find(x => x.filename === file.name)) {
      this._existingFiles.push(
        attachment ?? {
          embedded: false,
          filename: file.name,
          publicUrl: file.publicUrl ?? '',
          size: file.size ?? 0,
          virtualPath: ''
        }
      );
    }
  }

  private rebuildExistingFilesList() {
    this._existingFileProcess++;
    try {
      this.dropzoneFileObjects = [];
      this.existingFiles?.forEach(attachment => {
        this.dropzone?.displayExistingFile(
          {
            name: attachment.filename,
            size: attachment.size
          },
          '/img/undraw_rocket.svg'
        );
      });
    } finally {
      this._existingFileProcess--;
    }
  }

  public resetAttachments(attachments: ViewConversationAttachment[]) {
    if (compare(attachments, this.existingFiles)) return;
    this._existingFileProcess++;
    try {
      this.dropzone?.removeAllFiles();
      if (this.previews) this.previews.innerHTML = '';
      this._existingFiles = clone(attachments);
      this.dropzoneFileObjects = [];
      this.rebuildExistingFilesList();
      this.cleanUpProcessActivated = false;
    } finally {
      this._existingFileProcess--;
    }
  }

  protected dropZoneTemplate() {
    const uploadingTemplate = this.isUploading
      ? html`<span class="ms-3 fw-bold text-danger "><i class="fa-solid fa-spinner fa-spin"></i></span>`
      : html``;
    //class="border p-2 conversation-attachments-section border-top-0"
    const clickableClasses = `dz-clickable `;

    // The drop area is the paste target, so it holds focus and says so - matching the image
    // uploader. A focusable thing with no focus state is a secret rather than a feature.
    return html` <div class="conversation-attachments-container">
      <div
        class="row conversation-attachmments m-0"
        tabindex=${this.isReadonly ? -1 : 0}
        aria-label=${this.isReadonly
          ? nothing
          : LangDealerFranchiseeConversationWmConversationEntryDropzone.pasteHint()}
      >
        <div id="clickable" class=${clickableClasses}>
          <h2 class="m-2">${this.caption}${uploadingTemplate}</h2>
          <div class="image-upload-icon m-3">
            <i class="fa fa-solid fa-file-arrow-up"></i>
          </div>
        </div>
        <div id="previews" class="dropzone-previews"></div>
        ${this.isReadonly
          ? nothing
          : html`<div class="image-upload-paste-hint">
              ${LangDealerFranchiseeConversationWmConversationEntryDropzone.pasteHint()}
            </div>`}
      </div>
      <wm-context-menu .actions=${this.contextMenuActions()} zone=".conversation-attachmments"></wm-context-menu>
      <!--
        The picker is opened from this bar alone, not by clicking the whole zone, matching the
        image uploader. A drop zone that opens a file dialog wherever you click leaves no way to
        interact with it without a dialog appearing first.

        Readonly swaps the button for the caller's explanation but keeps the bar, so the tile holds
        its height and its boundary still lines up with the tile beside it. #upload-action itself is
        never conditional: Dropzone captures it once at init as its clickable element, so removing
        it would leave a zone that could not be re-armed if it stopped being readonly.
      -->
      <div class="image-upload-actions ${this.isReadonly ? 'image-upload-actions-readonly' : ''}">
        <div id="upload-action" class="upload-action-slot">
          ${this.isReadonly ? this.readonlyActionTemplate() : this.uploadButtonTemplate()}
        </div>
      </div>
      <div class="form-group row m-0">
        <div class="text-danger validation-msg">
          <div></div>
        </div>
      </div>
    </div>`;
  }

  /** Like the image uploader, the explanation is only worth showing while the zone is still empty. */
  private readonlyActionTemplate() {
    return this.existingFiles.length === 0 ? this.readonlyCaption : '';
  }

  private uploadButtonTemplate() {
    // No tabindex: the button is natively focusable, and the positive tabindex this replaced was an
    // accessibility anti-pattern that jumped the tab order.
    return html`<webmodule-button size="small" variant="default">
      <webmodule-icon library="fa" name="folder-open" slot="prefix"></webmodule-icon>
      ${this.maxFiles === 1
        ? LangDealerFranchiseeConversationWmConversationEntryDropzone.chooseFile()
        : LangDealerFranchiseeConversationWmConversationEntryDropzone.chooseFiles()}
    </webmodule-button>`;
  }

  protected render(): unknown {
    return this.dropZoneTemplate();
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
}
